import React, { useState } from "react";

import { CollapseSection } from "components";
import { LineDivider } from "components/layout";
import {
	CreditReviewInformationFields,
	daysToDeliveryFieldsNames,
	DaysToDeliveryFieldsNames,
} from "globalConstants";
import { setCreditReviewInformation } from "mappers";
import {
	commaAndFloat2Normalizer,
	float2Normalizer,
	floatWithComma2Normalizer,
	underwritingDaysToDeliverySum,
} from "validators";
import { maxValue100, onlyPercent } from "validators/validation";

import MerchantFieldForm from "./merchantFieldForm";

import styles from "./../autoCheckList.module.scss";

type MerchantInformationProps = {
	loading?: boolean;
	isEditableMerchantFieldOnHistorical?: boolean;
	isEditable: boolean;
	merchantInfoValues: { [key: string]: any };
};

const MerchantsInformation = ({
	loading,
	merchantInfoValues,
	isEditable,
	isEditableMerchantFieldOnHistorical,
}: MerchantInformationProps) => {
	const [creditReviewData, setCreditReviewData] = useState<{
		[key: string]: any;
	}>(setCreditReviewInformation(merchantInfoValues));

	const validateDaysToDelivery = (value, fields) =>
		underwritingDaysToDeliverySum({
			value,
			fields,
			daysToDeliveryNames: daysToDeliveryFieldsNames,
			daysToDeliveryData: creditReviewData,
		});

	const isFieldEditable = !isEditableMerchantFieldOnHistorical
		? false
		: isEditable;

	const {
		SameDay,
		OneThreeDays,
		FourSevenDays,
		EightFourteenDays,
		FifteenThirtyDays,
		ThirtyPlusDays,
	} = DaysToDeliveryFieldsNames;

	return (
		<CollapseSection
			header="Credit Review Information"
			className={`${styles.merchantInfoContainer}`}
			classNameHeader={styles.collapseHeader}
			classNameHeaderContent={styles.collapseHeaderContent}
			loading={loading}
		>
			<MerchantFieldForm
				label="Chargeback Volume, $"
				name={CreditReviewInformationFields.chargebackVolume}
				normalize={commaAndFloat2Normalizer}
				normalizeOnBlur={floatWithComma2Normalizer}
				isEditable={isFieldEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[floatWithComma2Normalizer]}
			/>
			<MerchantFieldForm
				label="Risk Exposure, $"
				name={CreditReviewInformationFields.riskExposure}
				normalize={commaAndFloat2Normalizer}
				normalizeOnBlur={floatWithComma2Normalizer}
				isEditable={false}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[floatWithComma2Normalizer]}
			/>
			<MerchantFieldForm
				label="Reserve, %"
				name={CreditReviewInformationFields.reserve}
				validate={{ maxValue100 }}
				normalize={onlyPercent}
				normalizeOnBlur={float2Normalizer}
				isEditable={isEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[float2Normalizer]}
			/>
			<MerchantFieldForm
				label="Reserve Amount, $"
				name={CreditReviewInformationFields.reserveAmount}
				normalize={commaAndFloat2Normalizer}
				normalizeOnBlur={floatWithComma2Normalizer}
				isEditable={isEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[floatWithComma2Normalizer]}
			/>
			<LineDivider />
			<div className="text-grey">Days to Delivery</div>
			<MerchantFieldForm
				label={`${SameDay.displayName}, %`}
				name={CreditReviewInformationFields.deliverySameDay}
				validate={{ maxValue100, validateDaysToDelivery }}
				normalize={onlyPercent}
				normalizeOnBlur={float2Normalizer}
				isEditable={isEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[float2Normalizer]}
			/>
			<MerchantFieldForm
				label={`${OneThreeDays.displayName}, %`}
				name={CreditReviewInformationFields.deliveryFromOneToThreeDays}
				validate={{ maxValue100, validateDaysToDelivery }}
				normalize={onlyPercent}
				normalizeOnBlur={float2Normalizer}
				isEditable={isEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[float2Normalizer]}
			/>
			<MerchantFieldForm
				label={`${FourSevenDays.displayName}, %`}
				name={CreditReviewInformationFields.deliveryFromFourToSevenDays}
				validate={{ maxValue100, validateDaysToDelivery }}
				normalize={onlyPercent}
				normalizeOnBlur={float2Normalizer}
				isEditable={isEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[float2Normalizer]}
			/>
			<MerchantFieldForm
				label={`${EightFourteenDays.displayName}, %`}
				name={
					CreditReviewInformationFields.deliveryFromEightToFourteenDays
				}
				validate={{ maxValue100, validateDaysToDelivery }}
				normalize={onlyPercent}
				normalizeOnBlur={float2Normalizer}
				isEditable={isEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[float2Normalizer]}
			/>
			<MerchantFieldForm
				label={`${FifteenThirtyDays.displayName}, %`}
				name={
					CreditReviewInformationFields.deliveryFromFifteenToThirtyDays
				}
				validate={{ maxValue100, validateDaysToDelivery }}
				normalize={onlyPercent}
				normalizeOnBlur={float2Normalizer}
				isEditable={isEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[float2Normalizer]}
			/>
			<MerchantFieldForm
				label={`${ThirtyPlusDays.displayName}, %`}
				name={CreditReviewInformationFields.deliveryOverThirtyDays}
				validate={{ maxValue100, validateDaysToDelivery }}
				normalize={onlyPercent}
				normalizeOnBlur={float2Normalizer}
				isEditable={isEditable}
				creditReviewData={creditReviewData}
				setCreditReviewData={setCreditReviewData}
				applicationId={merchantInfoValues?.applicationId}
				normalizer={[float2Normalizer]}
			/>
		</CollapseSection>
	);
};

export default MerchantsInformation;
